import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CPL3Settings } from "services/api/cplapi/CPLAPI";
import { ICPLSettingsService } from "services/CPLSettingsService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { toast } from "react-toastify";

type DirectDebitAdminFeeState = {
  IsLoading: boolean;
  IsSaving: boolean;
  DirectDebitAdminFeeAmount: number;
  DirectDebitAdminFeePostingDestination: string;
  DirectDebitAdminFeePostingFundHeader: string;
};

const initialState: DirectDebitAdminFeeState = {
  IsLoading: false,
  IsSaving: false,
  DirectDebitAdminFeeAmount: 0,
  DirectDebitAdminFeePostingDestination: "",
  DirectDebitAdminFeePostingFundHeader: "",
};

export const DirectDebitAdminFeeSlice = createSlice({
  name: "directDebitAdminFee",
  initialState,
  reducers: {
    setAdminFeeAmount: (state, action: PayloadAction<number>) => {
      state.DirectDebitAdminFeeAmount = action.payload;
    },
    setPostingDestination: (state, action: PayloadAction<string>) => {
      state.DirectDebitAdminFeePostingDestination = action.payload;
    },
    setPostingFundHeader: (state, action: PayloadAction<string>) => {
      state.DirectDebitAdminFeePostingFundHeader = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDirectDebitAdminFeeSettings.pending, (state) => {
        state.IsLoading = true;
      })
      .addCase(loadDirectDebitAdminFeeSettings.rejected, (state) => {
        state.IsLoading = false;
      })
      .addCase(loadDirectDebitAdminFeeSettings.fulfilled, (state, action) => {
        state.IsLoading = false;
        state.DirectDebitAdminFeeAmount = action.payload.amount;
        state.DirectDebitAdminFeePostingDestination = action.payload.postingDestination;
        state.DirectDebitAdminFeePostingFundHeader = action.payload.postingFundHeader;
      })
      .addCase(saveDirectDebitAdminFeeSettings.pending, (state) => {
        state.IsSaving = true;
      })
      .addCase(saveDirectDebitAdminFeeSettings.rejected, (state) => {
        state.IsSaving = false;
      })
      .addCase(saveDirectDebitAdminFeeSettings.fulfilled, (state) => {
        state.IsSaving = false;
        toast.success("Settings saved");
      });
  },
});

export const { setAdminFeeAmount, setPostingDestination, setPostingFundHeader } = DirectDebitAdminFeeSlice.actions;

export const loadDirectDebitAdminFeeSettings = createAsyncThunk("directDebitAdminFee/load", async () => {
  const service = container.get<ICPLSettingsService>(SERVICE_TYPES.CPLSettingsService);
  const [amountStr, postingDestination, postingFundHeader] = await Promise.all([
    service.getStringSetting(CPL3Settings.DirectDebitAdminFeeAmount),
    service.getStringSetting(CPL3Settings.DirectDebitAdminFeePostingDestination),
    service.getStringSetting(CPL3Settings.DirectDebitAdminFeePostingFundHeader),
  ]);
  return {
    amount: parseFloat(amountStr) || 0,
    postingDestination: postingDestination ?? "",
    postingFundHeader: postingFundHeader ?? "",
  };
});

export const saveDirectDebitAdminFeeSettings = createAsyncThunk("directDebitAdminFee/save", async (_, { getState }) => {
  const state = getState() as RootState;
  const { DirectDebitAdminFeeAmount, DirectDebitAdminFeePostingDestination, DirectDebitAdminFeePostingFundHeader } =
    state.directDebitAdminFee;
  const service = container.get<ICPLSettingsService>(SERVICE_TYPES.CPLSettingsService);
  await Promise.all([
    service.updateStringSetting(CPL3Settings.DirectDebitAdminFeeAmount, DirectDebitAdminFeeAmount.toFixed(2)),
    service.updateStringSetting(
      CPL3Settings.DirectDebitAdminFeePostingDestination,
      DirectDebitAdminFeePostingDestination
    ),
    service.updateStringSetting(
      CPL3Settings.DirectDebitAdminFeePostingFundHeader,
      DirectDebitAdminFeePostingFundHeader
    ),
  ]);
});

export const selectDirectDebitAdminFeeAmount = (state: RootState) =>
  state.directDebitAdminFee.DirectDebitAdminFeeAmount;
export const selectDirectDebitAdminFeePostingDestination = (state: RootState) =>
  state.directDebitAdminFee.DirectDebitAdminFeePostingDestination;
export const selectDirectDebitAdminFeePostingFundHeader = (state: RootState) =>
  state.directDebitAdminFee.DirectDebitAdminFeePostingFundHeader;
export const selectDirectDebitAdminFeeIsLoading = (state: RootState) => state.directDebitAdminFee.IsLoading;
export const selectDirectDebitAdminFeeIsSaving = (state: RootState) => state.directDebitAdminFee.IsSaving;

export default DirectDebitAdminFeeSlice.reducer;
